import { IconCheckmark } from '@components/icons/checkmark';
import { Menu, StandaloneMenuItem } from '@components/menu';
import { useScopedTranslate } from '@locale/translate';

import { EditableCellPopover, useInlineSave } from './editable-cell-popover';
import { EditableOptionMenuList } from './editable-option-menu-list';
import { editableCellLocale } from './locale';
import { EditableCellEditorProps, SelectCellValue } from './types';
import { useEditOptions } from './use-edit-options';
import { useEditableCellTrigger } from './use-editable-cell-trigger';
import { useOptionSearch } from './use-option-search';

import { typeRenderMap } from '../renderers';
import { isEmptyColumnValue } from '../table-utils';

type EditableSelectCellProps = EditableCellEditorProps<SelectCellValue | undefined>;

export const EditableSelectCell = ({ column, row, value }: EditableSelectCellProps) => {
  const { t } = useScopedTranslate(editableCellLocale);
  const getTrigger = useEditableCellTrigger();
  const { options, isLoading, hasError, load } = useEditOptions(column.getEditOptions);
  const search = useOptionSearch(options);
  const { error, save } = useInlineSave();

  const isEmpty = isEmptyColumnValue(value);
  const trigger = getTrigger(value, column, row, () =>
    typeRenderMap.single_select(value, column, row)
  );

  return (
    <EditableCellPopover
      trigger={trigger}
      column={column}
      row={row}
      value={value}
      isCommitOnClick
      validationError={error}
      onOpen={() => {
        load();
        search.reset();
      }}
    >
      {(close) => (
        <EditableOptionMenuList
          options={options}
          isLoading={isLoading}
          hasError={hasError}
          load={load}
          search={search}
        >
          {(filteredOptions) => (
            <>
              {/* The list is the whole editor now, so it carries the only way
                  to empty the cell - the same role "No user" plays for a user.
                  A column the API refuses to empty doesn't offer it. The
                  designs set it apart from the options with a separator: it
                  clears the cell rather than choosing something for it. */}
              {column.isClearable !== false && (
                <>
                  <StandaloneMenuItem
                    label={t('noOption')}
                    isActive={isEmpty}
                    iconRight={
                      isEmpty ? (
                        <IconCheckmark color="elementBlue100" data-c="editable-cell-checkmark" />
                      ) : null
                    }
                    onSelect={() =>
                      // The cell is already empty, so clearing it would only
                      // send a save that changes nothing.
                      isEmpty
                        ? close()
                        : save(async () => {
                            await column.onEdit?.(row, '');
                          }, close)
                    }
                    dataTestId="cell-option-none"
                  />
                  <Menu.Separator />
                </>
              )}
              {filteredOptions.map((option) => {
                const isActive = value?.shortId === option.value;

                return (
                  <StandaloneMenuItem
                    key={option.value}
                    label={option.label}
                    isActive={isActive}
                    iconRight={
                      isActive ? (
                        <IconCheckmark color="elementBlue100" data-c="editable-cell-checkmark" />
                      ) : null
                    }
                    onSelect={() =>
                      // Picking what the cell already holds is the click that
                      // closes the list, not a save.
                      isActive
                        ? close()
                        : save(async () => {
                            await column.onEdit?.(row, option.value);
                          }, close)
                    }
                  />
                );
              })}
            </>
          )}
        </EditableOptionMenuList>
      )}
    </EditableCellPopover>
  );
};
